import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Image } from '@agria/theme/src/components/Image/Image';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { useImages } from '@agria/theme/src/hooks/useImages';
import {
  BascLogo,
  GccfLogo,
  Logo,
  DogFriendlyLogo,
  CruftsLogo,
  InsureK9Logo,
  RdaLogo,
  CtmLogo,
  LogoSmall,
} from '@agria/theme/src/components/svg';

interface BrandLogoProps {
  brand?: string;
  companyName?: string;
}

const StyledDiv = styled.div`
  ${({ theme: { maxMedia } }) => css`
    flex: 1 0 auto;

    @media (max-width: ${maxMedia.xLarge}) {
      flex: 1;
    }
  `}
`;

const StyledLogoMobile = styled.div`
  ${({ theme: { media } }) => css`
    max-width: 200px;
    max-height: 48px;

    svg {
      max-width: 200px;
      min-height: 48px;
      width: 100%;
    }

    @media (min-width: ${media.large}) {
      display: none;
    }
  `};
`;

const StyledLogoDesktop = styled.div`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledLogo = styled(LinkTo)`
  ${({ theme: { maxMedia } }) => css`
    flex: 1 0 auto;

    // Add a little extra space between Agria logo and nav on Agria brochureware
    &[title='Agria Pet Insurance homepage'] + nav {
      margin-left: 16px;
    }

    @media (max-width: ${maxMedia.xLarge}) {
      flex: 1;
    }
  `}
`;

const StyledBrandLogo = styled.div`
  ${({ theme: { space, colors, maxMedia } }) => css`
    display: grid;
    place-items: center;
    padding-left: ${space.large};
    border-left: solid 1px ${colors.brandTwo20};
    height: 60px;

    & > div {
      height: unset;
    }

    svg {
      height: 100%;
    }

    .gatsby-image-wrapper {
      vertical-align: middle;
      img {
        object-fit: contain !important;
      }
    }

    img {
      max-width: 100%;
    }

    @media (max-width: ${maxMedia.small}) {
      padding-left: ${space.xSmall};
      height: 48px;
    }

    @media (max-width: ${maxMedia.xLarge}) {
      flex: 1;
    }
  `}
`;

const StyledBrandWideImageLogo = styled.div`
  ${({ theme: { space, colors, maxMedia } }) => css`
    padding-left: ${space.large};
    border-left: solid 1px ${colors.brandTwo20};
    height: 50px;

    .gatsby-image-wrapper {
      vertical-align: middle;
      img {
        object-fit: contain !important;
      }
    }

    @media (max-width: ${maxMedia.medium}) {
        width: 100% !important;
        height: 50px !important;
      }
    }

    @media (max-width: ${maxMedia.small}) {
      padding-left: ${space.xSmall};
      height: 48px;
    }

    @media (max-width: ${maxMedia.xLarge}) {
      flex: 1;
    }
  `}
`;

export const BrandLogo: FC<BrandLogoProps> = ({ brand, companyName }) => {
  const { cgLogo, dogFestLogo, pets4HomesLogo } = useImages();

  return (
    <>
      {brand === 'Insure K9' ? (
        <StyledDiv>
          <StyledLogoMobile>
            <LogoSmall />
          </StyledLogoMobile>
          <StyledLogoDesktop>
            <Logo />
          </StyledLogoDesktop>
        </StyledDiv>
      ) : (
        <StyledLogo to="/" title={`${companyName} homepage`}>
          <StyledLogoMobile>
            <LogoSmall title={`${companyName} homepage`} aria-hidden="true" />
          </StyledLogoMobile>
          <StyledLogoDesktop>
            <Logo title={`${companyName} homepage`} aria-hidden="true" />
          </StyledLogoDesktop>
        </StyledLogo>
      )}

      {brand === 'GCCF' && (
        <StyledBrandLogo>
          <GccfLogo />
        </StyledBrandLogo>
      )}

      {brand === 'BASC' && (
        <StyledBrandLogo>
          <BascLogo />
        </StyledBrandLogo>
      )}

      {brand === 'Dog Friendly' && (
        <StyledBrandLogo>
          <DogFriendlyLogo />
        </StyledBrandLogo>
      )}

      {brand === 'Crufts' && (
        <StyledBrandLogo>
          <CruftsLogo />
        </StyledBrandLogo>
      )}

      {brand === 'Insure K9' && (
        <StyledBrandLogo>
          <InsureK9Logo />
        </StyledBrandLogo>
      )}

      {brand === 'RDA' && (
        <StyledBrandLogo>
          <RdaLogo />
        </StyledBrandLogo>
      )}

      {brand === 'Clegg Gifford' && (
        <StyledBrandWideImageLogo>
          <Image src={cgLogo} alt="Clegg Gifford" />
        </StyledBrandWideImageLogo>
      )}

      {brand === 'CTM' && (
        <StyledBrandLogo>
          <CtmLogo />
        </StyledBrandLogo>
      )}

      {brand === 'DogFest' && (
        <StyledBrandLogo>
          <a
            href="https://www.dogfest.co.uk/?utm_source=agria&utm_medium=website"
            target="_blank"
            rel="noreferrer"
          >
            <Image src={dogFestLogo} alt="DogFest" loading="eager" />
          </a>
        </StyledBrandLogo>
      )}

      {brand === 'Pets4Homes' && (
        <StyledBrandLogo>
          <Image src={pets4HomesLogo} alt="Pets4Homes" loading="eager" />
        </StyledBrandLogo>
      )}
    </>
  );
};
